import { useEffect, useMemo, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import type { CurvaDocument } from "@/document/model/document";
import { getAllSceneNodes } from "@/document/model/document";
import { colorAtGradientPosition, createSolidPaint, type NodeEffect, type NodeStyle, type Paint } from "@/document/model/styles";
import type { OklchColor } from "@/document/model/color";
import type { DocumentStyle } from "@/document/model/documentStyles";
import { documentStyleUsageCount, findDocumentStyle } from "@/document/model/documentStyles";
import { resolvedLineHeight } from "@/document/model/text";
import { loadGoogleFontWeights } from "@/lib/GoogleFontLoader";
import type { ColorSamplerRequestWithCommit } from "../colorSampler";
import { ColorPickerPopover } from "./ColorPickerPopover";
import { DraggablePanel, stackedPanelInitialOffset } from "./DraggablePanel";
import { effectTypeLabel } from "./effectLabels";
import { EffectEditorPopover } from "./EffectEditorPopover";
import { EffectsSection } from "./EffectsSection";
import { PanelSectionAddButton } from "./PanelSection";
import { FillPaintStack, PaintSection } from "./PaintSection";
import {
  paintWithOpacity,
  styleWithAddedFill,
  styleWithAddedStroke,
  styleWithDuplicatedFill,
  styleWithPaint,
  styleWithReorderedFill,
  styleWithStrokeAlignAt,
  styleWithStrokeWidthAt,
  styleWithoutFill,
  styleWithoutStroke,
} from "./paintStyles";
import { StyleTypographyFields } from "./StyleTypographyFields";
import { documentStyleEditorTitle, documentStylePreviewBackground } from "./stylePreview";
import type { PaintTarget } from "./types";
import type { PickerMode } from "./types";

type StylePickerTarget = PaintTarget | { type: "effectColor"; effectId: string };
const FILL_REORDER_MIME = "application/x-curva-fill-index";

export function StyleEditorPopover({
  document,
  styleId,
  activeColorSamplerSourceId,
  onStyleChange,
  onStyleDelete,
  onColorSampleRequest,
  onColorSampleModeChange,
  onColorSampleCancel,
  onClose,
}: {
  document: CurvaDocument;
  styleId: string;
  activeColorSamplerSourceId?: string;
  onStyleChange: (style: DocumentStyle) => void;
  onStyleDelete: (styleId: string) => void;
  onColorSampleRequest: (request: ColorSamplerRequestWithCommit) => void;
  onColorSampleModeChange: (sourceId: string, mode: PickerMode) => void;
  onColorSampleCancel: () => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const style = findDocumentStyle(document, styleId);
  const usageCount = style ? documentStyleUsageCount(getAllSceneNodes(document), style.id) : 0;
  const [pickerTarget, setPickerTarget] = useState<StylePickerTarget | undefined>(undefined);
  const [editingEffectId, setEditingEffectId] = useState<string | undefined>(undefined);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      if (pickerTarget || editingEffectId || confirmDeleteOpen) {
        return;
      }
      onClose();
    }

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [confirmDeleteOpen, editingEffectId, onClose, pickerTarget]);

  const effectsNodeStyle = useMemo<NodeStyle | undefined>(() => {
    if (style?.kind !== "effects") {
      return undefined;
    }
    return { fills: [], strokes: [], opacity: 1, effects: style.effects };
  }, [style]);

  const editingEffect = effectsNodeStyle?.effects?.find(effect => effect.id === editingEffectId);
  const activePaint = resolveActivePaint(style, pickerTarget, editingEffect);

  if (!style) {
    return null;
  }

  return (
    <>
      <DraggablePanel
        panelRef={panelRef}
        storageKey={`style-editor-${style.kind}`}
        title={documentStyleEditorTitle(style)}
        widthClass="w-80"
        headerActions={
          <button
            type="button"
            aria-label={`Delete ${style.name}`}
            title="Delete style"
            onPointerDown={event => event.stopPropagation()}
            onClick={() => {
              if (usageCount > 0) {
                setConfirmDeleteOpen(true);
                return;
              }
              onStyleDelete(style.id);
            }}
            className="grid size-7 place-items-center rounded text-slate-500 hover:bg-white/10 hover:text-slate-200"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
          </button>
        }
        onClose={onClose}
      >
        <div className="space-y-3">
          <StylePreview style={style} document={document} />
          <StyleNameField
            name={style.name}
            onChange={name => onStyleChange({ ...style, name })}
          />
          <div className="space-y-2">
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">Properties</div>
            {style.kind === "fill" ? (
              <FillStyleProperties
                style={style}
                onColorClick={index => setPickerTarget({ type: "fill", index })}
                onReorder={() => setPickerTarget(undefined)}
                onStyleChange={onStyleChange}
              />
            ) : null}
            {style.kind === "stroke" ? (
              <StrokeStyleProperties
                style={style}
                onColorClick={index => setPickerTarget({ type: "stroke", index })}
                onStyleChange={onStyleChange}
              />
            ) : null}
            {style.kind === "text" ? (
              <StyleTypographyFields
                typography={style.typography}
                onChange={typography => onStyleChange({ ...style, typography })}
              />
            ) : null}
            {style.kind === "effects" && effectsNodeStyle ? (
              <EffectsSection
                style={effectsNodeStyle}
                onStyleChange={nextStyle => onStyleChange({ ...style, effects: nextStyle.effects ?? [] })}
                onEditEffect={setEditingEffectId}
              />
            ) : null}
          </div>
        </div>
      </DraggablePanel>

      {confirmDeleteOpen ? (
        <DeleteStyleConfirmation
          styleName={style.name}
          usageCount={usageCount}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => {
            setConfirmDeleteOpen(false);
            onStyleDelete(style.id);
          }}
        />
      ) : null}

      {activePaint && pickerTarget ? (
        <ColorPickerPopover
          title={pickerTitle(pickerTarget, editingEffect)}
          paint={activePaint}
          allowImage={isFillPickerTarget(pickerTarget)}
          activeColorSamplerSourceId={activeColorSamplerSourceId}
          onColorSampleRequest={onColorSampleRequest}
          onColorSampleModeChange={onColorSampleModeChange}
          onClose={() => {
            setPickerTarget(undefined);
            onColorSampleCancel();
          }}
          onChange={paint => {
            if (!style) {
              return;
            }
            if (isEffectPickerTarget(pickerTarget) && style.kind === "effects") {
              if (paint.type === "solid") {
                onStyleChange({
                  ...style,
                  effects: style.effects.map(effect => effect.id === pickerTarget.effectId
                    ? { ...effect, color: paint.color }
                    : effect),
                });
              } else if (paint.type === "linearGradient" || paint.type === "radialGradient") {
                onStyleChange({
                  ...style,
                  effects: style.effects.map(effect => effect.id === pickerTarget.effectId
                    ? { ...effect, color: colorAtGradientPosition(paint, 0) }
                    : effect),
                });
              }
              return;
            }
            if (style.kind === "fill" && isFillPickerTarget(pickerTarget)) {
              onStyleChange({
                ...style,
                fills: style.fills.map((candidate, index) => index === pickerTarget.index ? paint : candidate),
              });
              return;
            }
            if (style.kind === "stroke" && isStrokePickerTarget(pickerTarget)) {
              const strokes = [...style.strokes];
              const stroke = strokes[pickerTarget.index];
              if (stroke) {
                strokes[pickerTarget.index] = { ...stroke, paint };
                onStyleChange({ ...style, strokes });
              }
            }
          }}
        />
      ) : null}

      {style.kind === "effects" && effectsNodeStyle && editingEffect ? (
        <EffectEditorPopover
          style={effectsNodeStyle}
          effect={editingEffect}
          suspendDismiss={isEffectPickerTarget(pickerTarget) && pickerTarget.effectId === editingEffect.id}
          onStyleChange={nextStyle => onStyleChange({ ...style, effects: nextStyle.effects ?? [] })}
          onColorClick={() => setPickerTarget({ type: "effectColor", effectId: editingEffect.id })}
          onClose={() => setEditingEffectId(undefined)}
        />
      ) : null}
    </>
  );
}

function DeleteStyleConfirmation({
  styleName,
  usageCount,
  onCancel,
  onConfirm,
}: {
  styleName: string;
  usageCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onCancel();
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
      if (event.key === "Enter") {
        event.preventDefault();
        onConfirm();
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel, onConfirm]);

  return (
    <DraggablePanel
      panelRef={panelRef}
      storageKey="delete-style-confirmation"
      title="Delete style"
      widthClass="w-72"
      initialOffset={stackedPanelInitialOffset}
      onClose={onCancel}
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <div className="text-[12px] font-medium text-slate-100">Delete "{styleName}"?</div>
          <p className="text-[12px] leading-5 text-slate-400">
            This style is applied to {usageCount} {usageCount === 1 ? "object" : "objects"}. Curva will keep their appearance by detaching the style before deleting it.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded px-2.5 py-1.5 text-[11px] text-slate-400 hover:bg-white/[0.05] hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded bg-red-500/15 px-2.5 py-1.5 text-[11px] font-medium text-red-200 hover:bg-red-500/25"
          >
            Delete
          </button>
        </div>
      </div>
    </DraggablePanel>
  );
}

function StylePreview({ style, document }: { style: DocumentStyle; document: CurvaDocument }) {
  if (style.kind === "text") {
    return <TextStylePreview typography={style.typography} />;
  }

  if (style.kind === "fill") {
    const background = documentStylePreviewBackground(document, style);
    return (
      <div className="flex h-24 items-center justify-center rounded bg-[#2f343a] px-4">
        <div className="h-14 w-full max-w-[12rem] rounded-md border border-white/10" style={{ background }} />
      </div>
    );
  }

  if (style.kind === "stroke") {
    const stroke = style.strokes.at(-1);
    const paint = stroke?.paint;
    const borderColor = paint?.type === "solid" ? undefined : "transparent";
    const background = paint ? documentStylePreviewBackground(document, { ...style, kind: "stroke" }) : "transparent";
    return (
      <div className="flex h-24 items-center justify-center rounded bg-[#2f343a] px-4">
        <div
          className="h-14 w-full max-w-[12rem] rounded-md bg-transparent"
          style={{
            borderWidth: Math.max(1, stroke?.width ?? 1),
            borderStyle: "solid",
            borderColor: borderColor ?? background,
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-24 items-center justify-center rounded px-4 text-[12px] text-slate-300"
      style={{ background: documentStylePreviewBackground(document, style) }}
    >
      {style.effects.length} effect{style.effects.length === 1 ? "" : "s"}
    </div>
  );
}

function TextStylePreview({ typography }: { typography: Extract<DocumentStyle, { kind: "text" }>["typography"] }) {
  useEffect(() => {
    void loadGoogleFontWeights(typography.fontFamily);
  }, [typography.fontFamily]);

  const lineHeight = resolvedLineHeight(typography);
  return (
    <div className="flex h-24 items-center justify-center rounded bg-[#2f343a] px-4">
      <span
        className="text-white"
        style={{
          fontFamily: typography.fontFamily,
          fontWeight: typography.fontWeight,
          fontSize: typography.fontSize,
          lineHeight: `${lineHeight}px`,
          letterSpacing: typography.letterSpacing,
        }}
      >
        Rag 123
      </span>
    </div>
  );
}

function StyleNameField({ name, onChange }: { name: string; onChange: (name: string) => void }) {
  return (
    <label className="flex items-center gap-3">
      <span className="w-16 shrink-0 text-[11px] text-slate-500">Name</span>
      <input
        type="text"
        value={name}
        onChange={event => onChange(event.target.value)}
        className="h-8 min-w-0 flex-1 rounded border border-white/10 bg-white/[0.04] px-2 text-[12px] text-slate-100 outline-none focus:border-white/20"
      />
    </label>
  );
}

function FillStyleProperties({
  style,
  onColorClick,
  onReorder,
  onStyleChange,
}: {
  style: Extract<DocumentStyle, { kind: "fill" }>;
  onColorClick: (index: number) => void;
  onReorder: () => void;
  onStyleChange: (style: DocumentStyle) => void;
}) {
  const nodeStyle = { fills: style.fills, strokes: [], opacity: 1 };

  return (
    <div className="grid gap-2">
      {style.fills.length === 0 ? (
        <PanelSectionAddButton
          label="Add fill"
          onClick={() => onStyleChange({ ...style, fills: [createSolidPaint("#f8fafc")] })}
        />
      ) : (
        <FillPaintStack
          fills={style.fills}
          mimeType={FILL_REORDER_MIME}
          onReorder={(fromIndex, hoverIndex, placement) => {
            const nextStyle = styleWithReorderedFill(nodeStyle, fromIndex, hoverIndex, placement);
            onStyleChange({ ...style, fills: nextStyle.fills });
            onReorder();
          }}
        >
          {({ index, paint, reorder }) => (
            <PaintSection
              key={paint.id}
              title={index === style.fills.length - 1 ? "Fill" : `Fill ${index + 1}`}
              disabled={false}
              paint={paint}
              compactTitle
              hideCompactTitle
              reorder={reorder}
              onColorClick={() => onColorClick(index)}
              onDuplicate={() => {
                const nextStyle = styleWithDuplicatedFill(nodeStyle, index);
                onStyleChange({ ...style, fills: nextStyle.fills });
              }}
              onRemove={() => {
                const nextStyle = styleWithoutFill(nodeStyle, index);
                onStyleChange({ ...style, fills: nextStyle.fills });
              }}
              onOpacityCommit={opacity => {
                const nextPaint = paintWithOpacity(paint, opacity);
                if (nextPaint) {
                  const nextStyle = styleWithPaint(nodeStyle, { type: "fill", index }, nextPaint);
                  onStyleChange({ ...style, fills: nextStyle.fills });
                }
              }}
              onColorCommit={color => {
                const nextStyle = styleWithPaint(nodeStyle, { type: "fill", index }, color);
                onStyleChange({ ...style, fills: nextStyle.fills });
              }}
            />
          )}
        </FillPaintStack>
      )}
      {style.fills.length > 0 ? (
        <PanelSectionAddButton
          label="Add fill"
          onClick={() => {
            const nextStyle = styleWithAddedFill(nodeStyle);
            onStyleChange({ ...style, fills: nextStyle.fills });
          }}
        />
      ) : null}
    </div>
  );
}

function StrokeStyleProperties({
  style,
  onColorClick,
  onStyleChange,
}: {
  style: Extract<DocumentStyle, { kind: "stroke" }>;
  onColorClick: (index: number) => void;
  onStyleChange: (style: DocumentStyle) => void;
}) {
  const nodeStyle = { fills: [], strokes: style.strokes, opacity: 1 };

  return (
    <div className="grid gap-2">
      {style.strokes.length === 0 ? (
        <PanelSectionAddButton
          label="Add stroke"
          onClick={() => {
            const nextStyle = styleWithAddedStroke(nodeStyle);
            onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
          }}
        />
      ) : (
        style.strokes.map((_, index) => index).reverse().map(index => {
          const stroke = style.strokes[index];
          if (!stroke) {
            return null;
          }
          return (
            <PaintSection
              key={`stroke-${index}`}
              title={index === style.strokes.length - 1 ? "Stroke" : `Stroke ${index + 1}`}
              disabled={false}
              paint={stroke.paint}
              width={stroke.width}
              align={stroke.align ?? "inside"}
              compactTitle
              onColorClick={() => onColorClick(index)}
              onRemove={() => {
                const nextStyle = styleWithoutStroke(nodeStyle, index);
                onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
              }}
              onOpacityCommit={opacity => {
                const nextPaint = paintWithOpacity(stroke.paint, opacity);
                if (nextPaint) {
                  const nextStyle = styleWithPaint(nodeStyle, { type: "stroke", index }, nextPaint);
                  onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
                }
              }}
              onWidthCommit={width => {
                const nextStyle = styleWithStrokeWidthAt(nodeStyle, index, width);
                onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
              }}
              onAlignCommit={align => {
                const nextStyle = styleWithStrokeAlignAt(nodeStyle, index, align);
                onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
              }}
              onColorCommit={color => {
                const nextStyle = styleWithPaint(nodeStyle, { type: "stroke", index }, color);
                onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
              }}
            />
          );
        })
      )}
      {style.strokes.length > 0 ? (
        <PanelSectionAddButton
          label="Add stroke"
          onClick={() => {
            const nextStyle = styleWithAddedStroke(nodeStyle);
            onStyleChange({ ...style, strokes: nextStyle.strokes ?? [] });
          }}
        />
      ) : null}
    </div>
  );
}

function resolveActivePaint(
  style: DocumentStyle | undefined,
  pickerTarget: StylePickerTarget | undefined,
  editingEffect: NodeEffect | undefined,
): Paint | undefined {
  if (!style || !pickerTarget) {
    return undefined;
  }
  if (isEffectPickerTarget(pickerTarget) && editingEffect && (editingEffect.type === "dropShadow" || editingEffect.type === "innerShadow")) {
    return createSolidPaint(editingEffect.color);
  }
  if (style.kind === "fill" && isFillPickerTarget(pickerTarget)) {
    return style.fills[pickerTarget.index];
  }
  if (style.kind === "stroke" && isStrokePickerTarget(pickerTarget)) {
    return style.strokes[pickerTarget.index]?.paint;
  }
  return undefined;
}

function pickerTitle(pickerTarget: StylePickerTarget, editingEffect: NodeEffect | undefined): string {
  if (isEffectPickerTarget(pickerTarget) && editingEffect) {
    return effectTypeLabel(editingEffect.type);
  }
  return isFillPickerTarget(pickerTarget) ? "Fill" : "Stroke";
}

function isFillPickerTarget(target: StylePickerTarget | undefined): target is { type: "fill"; index: number } {
  return typeof target === "object" && target.type === "fill";
}

function isStrokePickerTarget(target: StylePickerTarget | undefined): target is { type: "stroke"; index: number } {
  return typeof target === "object" && target.type === "stroke";
}

function isEffectPickerTarget(target: StylePickerTarget | undefined): target is { type: "effectColor"; effectId: string } {
  return typeof target === "object" && target.type === "effectColor";
}
